import { createInertiaApp, router } from "@inertiajs/vue3";
import type { Page } from "@inertiajs/core";
import { Capacitor } from "@capacitor/core";
import * as Sentry from "@sentry/vue";
import { createPinia } from "pinia";
import { createApp, h } from "vue";
import "../css/app.css";
import {
  applyLockedLocaleCookie,
  createI18nPlugin,
  isLocaleLockedToPortuguese,
  LOCKED_LOCALE,
  resolveAppLocale,
} from "./i18n";
import {
  registerMobileOfflineAuthCache,
  seedOfflineAuthFromInitialPage,
} from "./mobile/auth/offlineAuthCache";
import { isBundledMobileApp } from "./mobile/native/apiBase";
import { prepareBundledMobileShell } from "./mobile/native/bundledApp";
import { installBundledVueErrorHandler } from "./mobile/native/bundledErrorOverlay";
import { registerBundledNavigationGuard } from "./mobile/native/bundledNavigationGuard";
import { registerMobileBackButton } from "./mobile/native/registerMobileBackButton";
import { loadMobileApiToken } from "./mobile/native/mobileAuthToken";
import {
  initializeFieldSync,
  startFieldSyncWatcher,
} from "./mobile/sync/useFieldSync";
import { registerOfflineNavigationGuard } from "./mobile/offline/offlineNavigationGuard";
import { prefetchFieldInertiaPages } from "./mobile/offline/prefetchFieldPages";
import {
  loadSyncMeta,
  startSyncNetworkWatcher,
} from "./services/SyncService";
import { useCapacitor } from "./shared/composables/useCapacitor";
import { resolveInertiaPage } from "./pages";
import { initializeTheme } from "./shared/composables/useAppearance";
import {
  installAxiosCsrfRetry,
  installCsrfFetchRetry,
} from "./shared/csrfFetch";

const appName = import.meta.env.VITE_APP_NAME || "EngeView Mobile";
const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const isSafari =
  typeof navigator !== "undefined" &&
  (navigator.vendor?.includes("Apple") ?? false);

function registerServiceWorker() {
  if (import.meta.env.VITE_MOBILE_BUNDLED === "true") {
    return;
  }

  if (typeof window === "undefined" || !window.isSecureContext) {
    return;
  }

  void import("virtual:pwa-register").then(({ registerSW }) => {
    registerSW({
      immediate: true,
      onRegisteredSW(_url, registration) {
        registration?.update();
      },
      onNeedRefresh() {
        window.location.reload();
      },
      onOfflineReady() {
        console.info("[PWA] App pronto para uso offline.");
      },
    });
  });
}

async function refreshBundledOfflinePackageInBackground(): Promise<void> {
  if (import.meta.env.VITE_MOBILE_BUNDLED !== "true" || !Capacitor.isNativePlatform()) {
    return;
  }

  const token = await loadMobileApiToken();
  if (!token) {
    return;
  }

  const { Network } = await import("@capacitor/network");
  const status = await Network.getStatus();

  if (status.connected) {
    const { scheduleBundledOfflineBackgroundPackage } = await import(
      "./mobile/offline/bundledOfflineBackground"
    );
    scheduleBundledOfflineBackgroundPackage();
  }
}

async function bootstrapApplication(): Promise<void> {
  applyLockedLocaleCookie();

  const isBundledBuild = import.meta.env.VITE_MOBILE_BUNDLED === "true";

  if (isBundledBuild) {
    try {
      await prepareBundledMobileShell();
    } catch (error) {
      console.error("[bootstrap] Falha ao preparar shell mobile:", error);
    }
    void refreshBundledOfflinePackageInBackground();
  }

  installCsrfFetchRetry();
  installAxiosCsrfRetry();

  if (!isBundledBuild) {
    registerServiceWorker();
  }

  createInertiaApp({
    title: (title) => (title ? `${title} - ${appName}` : appName),
    resolve: resolveInertiaPage,
    setup({ el, App, props, plugin }) {
      registerMobileOfflineAuthCache();
      if (isBundledBuild) {
        registerBundledNavigationGuard();
        registerMobileBackButton();
      }
      registerOfflineNavigationGuard();
      void seedOfflineAuthFromInitialPage(props.initialPage as Page);

      const { showMobileFieldShell, isNative } = useCapacitor();
      if (showMobileFieldShell || isNative) {
        startFieldSyncWatcher();
        void initializeFieldSync().then(() => {
          if (!isBundledMobileApp()) {
            void prefetchFieldInertiaPages();
          }
        });
        startSyncNetworkWatcher();
        void loadSyncMeta();
      }

      const i18n = createI18nPlugin(resolveAppLocale(props.initialPage as Page));

      if (isLocaleLockedToPortuguese()) {
        router.on("success", () => {
          i18n.global.locale.value = LOCKED_LOCALE;
        });
      }

      const app = createApp({ render: () => h(App, props) })
        .use(plugin)
        .use(i18n)
        .use(createPinia());

      if (isBundledBuild) {
        installBundledVueErrorHandler(app);
      }

      if (sentryDsn && !isSafari) {
        Sentry.init({
          app,
          dsn: sentryDsn,
          integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
              maskAllText: true,
              blockAllMedia: true,
            }),
          ],
          tracesSampleRate: 0.2,
          replaysSessionSampleRate: 0.1,
          replaysOnErrorSampleRate: 1.0,
          environment: import.meta.env.MODE,
        });

        if (isBundledBuild) {
          Sentry.setTag('mobile.bundled', 'true');
          const buildId = import.meta.env.VITE_MOBILE_BUILD_ID as string | undefined;
          if (buildId) {
            Sentry.setTag('mobile.build_id', buildId);
          }
        }
      }

      app.mount(el);
    },
    progress: {
      color: "#4B5563",
    },
  });
}

void bootstrapApplication().catch((error) => {
  // Último recurso: em vez de tela branca, mostra o erro para diagnóstico no dispositivo.
  console.error("[bootstrap] erro fatal ao iniciar o app:", error);

  const el = document.getElementById("app");
  if (el && !el.innerHTML.trim()) {
    const message = error instanceof Error ? `${error.message}` : String(error);
    el.innerHTML = `
      <div style="padding:24px;font-family:system-ui,sans-serif;color:#1f2937;line-height:1.5">
        <h1 style="font-size:18px;font-weight:700;margin:0 0 8px">Não foi possível abrir o app</h1>
        <p style="margin:0 0 12px">Tente fechar e abrir novamente. Se persistir, envie esta mensagem ao suporte:</p>
        <pre style="white-space:pre-wrap;background:#f3f4f6;padding:12px;border-radius:8px;font-size:12px">${message}</pre>
      </div>`;
  }
});

// This will set light / dark mode on page load...
initializeTheme();
